import { useMutation } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { apiClient } from '../../lib/api-client';

function apiMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
  return data?.message ?? "Lien d'invitation expiré ou invalide.";
}

// Défini hors du composant : sinon il serait recréé à chaque rendu, ce qui
// remonterait les champs et leur ferait perdre le focus à chaque frappe.
function Card({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
        {children}
      </div>
    </main>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => apiClient.post('/users/accept-invite', { token, password }),
    onError: (err) => setError(apiMessage(err)),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('Le mot de passe doit faire au moins 12 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    mutation.mutate();
  }

  if (mutation.isSuccess) {
    return (
      <Card>
        <h1 className="text-xl font-semibold text-slate-900">Compte activé ✓</h1>
        <p className="mt-2 text-sm text-slate-500">
          Votre mot de passe a été défini. Vous pouvez maintenant vous connecter.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-block rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700"
        >
          Aller à la connexion
        </Link>
      </Card>
    );
  }

  if (!token) {
    return (
      <Card>
        <h1 className="text-lg font-semibold text-slate-900">Lien invalide</h1>
        <p className="mt-2 text-sm text-slate-500">
          Ce lien d'invitation est incomplet ou a expiré. Demandez à votre administrateur de vous
          renvoyer une invitation.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-block text-sm font-medium text-brand hover:underline"
        >
          Retour à la connexion
        </Link>
      </Card>
    );
  }

  return (
    <Card>
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-semibold text-slate-900">Activez votre compte</h1>
        <p className="mt-2 text-sm text-slate-500">
          Choisissez un mot de passe pour accéder au Dashboard Adweb-Systèmes.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div>
          <label htmlFor="ai-password" className="mb-1 block text-sm font-medium text-slate-700">
            Mot de passe (12 caractères minimum)
          </label>
          <input
            id="ai-password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>
        <div>
          <label htmlFor="ai-confirm" className="mb-1 block text-sm font-medium text-slate-700">
            Confirmer le mot de passe
          </label>
          <input
            id="ai-confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60"
        >
          {mutation.isPending ? 'Activation…' : 'Activer mon compte'}
        </button>
      </form>
    </Card>
  );
}
